// frontend/src/context/AuthContext.js

import React, { createContext, useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
export const AuthContext = createContext();

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  //logger.log("AuthContext: token from localStorage:", token);

  const setTokenWithLoading = useCallback((newToken) => {
    if (newToken !== token) {
      setLoading(true); // Set loading when token changes
      setToken(newToken);
      if (newToken) {
        localStorage.setItem("token", newToken);
      } else {
        localStorage.removeItem("token");
      }
    }
  }, [token]);

  const verifyToken = useCallback(async (retryCount = 0) => {
    // ── Admin Route Bypass ──
    // Do not verify student auth on admin pages. AdminContext handles its own security.
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }
    // ────────────────────────

    try {
      const res = await axios.get(`${API_BASE_URL}/auth/verify-token`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setUser(res.data.user);
    } catch (err) {
      if (err.response?.status === 429 && retryCount < 3) {
        console.warn(`Rate limited on verify-token. Retrying in 2 seconds... (Attempt ${retryCount + 1}/3)`);
        setTimeout(() => verifyToken(retryCount + 1), 2000);
        return;
      }
      // 401 = no valid session (expected when not logged in) — clear state silently
      if (err.response?.status !== 401) {
        console.error("Token verification failed:", err);
      }
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
      if (err.response?.status === 429) {
        window.location.href = '/login?error=rate_limited';
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // New function to explicitly update user.onboarding_completed in state
  // This is called AFTER the modal is closed on the OnboardingPage
  const updateOnboardingStatus = async () => {
    // Get the latest token from localStorage in case it was updated
    const latestToken = localStorage.getItem("token");
    if (latestToken && latestToken !== token) {
      setToken(latestToken);
      return; // The useEffect will handle verification
    }

    // Otherwise, just verify with current token
    await verifyToken();
  };

  const completeOnboarding = async (formData) => {
    setLoading(true); // Keep loading true during API call
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/complete-onboarding`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      //logger.log("Onboarding complete response:", res.data);

      // IMPORTANT CHANGE:
      // If the backend returns a new token after onboarding completion,
      // store it in localStorage but don't immediately update the token state
      if (res.data.token && res.data.token !== token) {
        localStorage.setItem("token", res.data.token);
      }

      // CRITICAL FIX: Set loading to false so the success modal can show
      setLoading(false);

      return { success: true, message: res.data.msg };
    } catch (err) {
      console.error(
        "Failed to complete onboarding:",
        err.response?.data || err.message
      );

      // CRITICAL FIX: Set loading to false even on error
      setLoading(false);

      return {
        success: false,
        message: err.response?.data?.msg || "An error occurred",
      };
    }
  };

  // Logout function — calls backend to expire the HTTP-only cookie
  const logout = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
    } catch {
      // Backend logout failure is non-fatal — clear client state regardless
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
  };

  // NOTE: Token-from-URL reader removed.
  // The backend now delivers the JWT as an HTTP-only cookie during Google OAuth.
  // The cookie is sent automatically with withCredentials:true — no URL param needed.

  // ── Free access period helpers (derived from user object) ─────────────────
  // Sourced from /auth/verify-token response so they stay fresh after each load.
  const freeAccessPeriod = user?.free_access_period ?? false;
  const freeAccessMessage = user?.free_access_message ?? "";
  // ───────────────────────────────────────────────────────────────────────

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        setToken: setTokenWithLoading,
        setUser,            // exposed so AuthCallback can hydrate user directly
        completeOnboarding,
        updateOnboardingStatus,
        logout,
        freeAccessPeriod,
        freeAccessMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
